import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import { SITE, IMAGES } from "../data/site";

const line = {
  hidden: { y: "110%" },
  visible: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] } }),
};

const HEADLINE = ["A safe haven for", "emotional clarity", "& quiet strength."];

export const Hero = ({ onBook }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="relative pt-32 sm:pt-40 pb-16 overflow-hidden">
      <motion.div style={{ y: y2 }} className="pointer-events-none absolute -top-10 -right-20 w-[420px] h-[420px] rounded-full bg-[#D98BA0]/30 blur-3xl" />
      <motion.div style={{ y: y1 }} className="pointer-events-none absolute top-40 -left-24 w-[360px] h-[360px] rounded-full bg-[#D8E5ED]/50 blur-3xl" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center relative">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full bg-[#FFEFDF] border border-[#FFC9A0] px-4 py-1.5 mb-6">
            <span className="w-2 h-2 rounded-full bg-[#3A7D32] animate-pulse" />
            <span className="overline text-[0.62rem]">Counselling · {SITE.role}</span>
          </motion.div>

          <motion.h1 data-testid="hero-brand" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="brand-script text-6xl sm:text-7xl lg:text-[7.5rem] leading-[0.95] mb-6">
            Serene Mind Place
          </motion.h1>

          <h2 data-testid="hero-headline" className="section-title text-3xl sm:text-4xl lg:text-5xl leading-[1.08] text-[#20301A]">
            {HEADLINE.map((ln, i) => (
              <span key={i} className="mask-line">
                <motion.span custom={i} variants={line} initial="hidden" animate="visible" className="block">
                  {i === 1 ? <em className="italic text-[#3A7D32]">{ln}</em> : ln}
                </motion.span>
              </span>
            ))}
          </h2>

          <motion.p data-testid="hero-subtext" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-[#55703F] leading-relaxed">
            Personalised therapy with <span className="text-[#3A7D32] font-medium">{SITE.therapist}</span> in Jubilee Hills, Hyderabad —
            a warm, unjudgmental space for everything from everyday stress and relationship concerns to major clinical mental health disorders.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-4">
            <button data-testid="hero-book-cta" onClick={onBook}
              className="group inline-flex items-center gap-2 rounded-full bg-[#3A7D32] text-[#F2F8E8] px-7 py-3.5 text-sm hover:bg-[#20301A] transition-all duration-300 hover:scale-[1.03]">
              Book a Consultation
              <ArrowUpRight size={17} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
          </motion.div>
        </div>

        <motion.div style={{ y: y2 }} className="lg:col-span-5">
          <motion.div initial={{ opacity: 0, scale: 0.94, rotate: -2 }} animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            whileHover={{ rotate: -1.5, scale: 1.01 }}
            className="relative rounded-[28px] overflow-hidden soft-shadow border border-[#C9DFAC]">
            <img src={IMAGES.office} alt="Serene counselling space" className="w-full h-[440px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#20301A]/45 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 glass rounded-2xl p-4 flex items-center gap-3 border border-white/40">
              <MapPin size={18} className="text-[#3A7D32] shrink-0" />
              <div>
                <div className="text-xs font-medium text-[#20301A]">Jubilee Hills, Hyderabad</div>
                <div className="text-[0.7rem] text-[#55703F]">{SITE.hours}</div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
